import { THEME_MODES, THEMES } from "../constants";
import React from "react";

const THEME_MODE_STORAGE_KEY = "themeMode";
const LEGACY_THEME_STORAGE_KEY = "theme";

const getSystemThemeMode = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return THEME_MODES.LIGHT;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? THEME_MODES.DARK
    : THEME_MODES.LIGHT;
};

const resolveThemeFromMode = (themeMode, systemThemeMode) => {
  const effectiveMode =
    themeMode === THEME_MODES.SYSTEM ? systemThemeMode : themeMode;

  return effectiveMode === THEME_MODES.DARK
    ? THEMES.PODIUM_DARK
    : THEMES.PODIUM;
};

const isThemeMode = (value) => Object.values(THEME_MODES).includes(value);

const getLegacyThemeMode = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const savedLegacyTheme = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);

  if (!savedLegacyTheme) {
    return null;
  }

  try {
    const parsedLegacyTheme = JSON.parse(savedLegacyTheme);

    if (
      parsedLegacyTheme === THEMES.DARK ||
      parsedLegacyTheme === THEMES.PODIUM_DARK
    ) {
      return THEME_MODES.DARK;
    }

    if (Object.values(THEMES).includes(parsedLegacyTheme)) {
      return THEME_MODES.LIGHT;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const getInitialThemeMode = () => {
  if (typeof window === "undefined") {
    return THEME_MODES.SYSTEM;
  }

  const savedThemeMode = localStorage.getItem(THEME_MODE_STORAGE_KEY);

  if (savedThemeMode) {
    try {
      const parsedThemeMode = JSON.parse(savedThemeMode);

      if (isThemeMode(parsedThemeMode)) {
        return parsedThemeMode;
      }
    } catch (error) {
      return THEME_MODES.SYSTEM;
    }
  }

  return getLegacyThemeMode() || THEME_MODES.SYSTEM;
};

const initialState = {
  resolvedTheme: THEMES.PODIUM,
  setTheme: (nextTheme) => {
    void nextTheme;
  },
  setThemeMode: (nextThemeMode) => {
    void nextThemeMode;
  },
  theme: THEMES.PODIUM,
  themeMode: THEME_MODES.SYSTEM,
};
const ThemeContext = React.createContext(initialState);

function ThemeProvider({ children }) {
  const [themeMode, _setThemeMode] = React.useState(getInitialThemeMode);
  const [systemThemeMode, setSystemThemeMode] =
    React.useState(getSystemThemeMode);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemThemeMode(event.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT);
    };

    setSystemThemeMode(
      mediaQuery.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT
    );

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);

      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);

    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const resolvedTheme = React.useMemo(
    () => resolveThemeFromMode(themeMode, systemThemeMode),
    [themeMode, systemThemeMode]
  );

  const setThemeMode = (nextThemeMode) => {
    if (!isThemeMode(nextThemeMode)) {
      return;
    }

    localStorage.setItem(THEME_MODE_STORAGE_KEY, JSON.stringify(nextThemeMode));
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
    _setThemeMode(nextThemeMode);
  };

  const setTheme = (nextTheme) => {
    if (isThemeMode(nextTheme)) {
      setThemeMode(nextTheme);
      return;
    }

    if (nextTheme === THEMES.DARK || nextTheme === THEMES.PODIUM_DARK) {
      setThemeMode(THEME_MODES.DARK);
      return;
    }

    if (Object.values(THEMES).includes(nextTheme)) {
      setThemeMode(THEME_MODES.LIGHT);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        resolvedTheme,
        setTheme,
        setThemeMode,
        theme: resolvedTheme,
        themeMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export { ThemeProvider, ThemeContext };
